import React, { useEffect, useState } from 'react';
import { Button, Checkbox, ClearableInput, Notification, Spinner } from '@rio-cloud/rio-uikit';
import { fieldLabelsSubject, fieldOptionsSubject } from '../../../../../support/constants';
import { createSubtopicSupport, editSubtopicSupport } from '../../../../../../api/tas';
import { Field, IResponseSubjectData } from '../List';
import { useIntl } from 'react-intl';

interface CreateSubtopicForm {
    onSuccess: () => void;
    onCancel: () => void;
    data?: IResponseSubjectData | null;
}

interface SubTopicForm {
    code: string,
    disabled: boolean;
    name: string,
}

const SubTopicForm: React.FC<CreateSubtopicForm> = ({
    onSuccess,
    onCancel,
    data,
}) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const hasEdit = !!data?.id;
    const [formData, setFormData] = useState<SubTopicForm>({
        code: '',
        disabled: false,
        name: '',
    });

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name) {
            newErrors.name = 'Obrigatório';
        }

        if (!formData.code) {
            newErrors.code = 'Obrigatório';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);



        try {
            if (data?.id) {
                await editSubtopicSupport(data?.id, formData);
            } else {
                await createSubtopicSupport(formData);
            }
            onSuccess();
            Notification.success(
                hasEdit
                    ? intl.formatMessage({ id: 'intl-msg:tas.subtopic.success-edit' })
                    : intl.formatMessage({ id: 'intl-msg:tas.subtopic.success-create' })
            );

        } catch (error) {

            Notification.error(
                hasEdit
                    ? intl.formatMessage({ id: 'intl-msg:tas.subtopic.error-edit' })
                    : intl.formatMessage({ id: 'intl-msg:tas.subtopic.error-create' })
            );
        } finally {
            setLoading(false);
        }

    };

    const handleInputChange = (field: keyof SubTopicForm, value: string | string[]) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    useEffect(() => {
        if (data) {
            setFormData({
                code: data.code,
                disabled: data.disabled,
                name: data.name,
            })
        } else {
            setFormData(prev => ({
                ...prev,
                fields: fieldOptionsSubject,
            }))
        }
    }, [data])

    const intl = useIntl();

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="name" className="block font-medium text-gray-700 mb-2">
                        {intl.formatMessage({ id: "intl-msg:backoffice-support-table-name" })} *
                    </label>
                    <ClearableInput id='name' value={formData.name} onChange={(v) => handleInputChange('name', v)} />
                    {errors.name && (
                        <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="code" className="block font-medium text-gray-700 mb-2">
                        {intl.formatMessage({ id: "intl-msg:backoffice-support-table-code" })} *
                    </label>
                    <ClearableInput id='code' value={formData.code} disabled={!!data?.code} onChange={(v) => handleInputChange('code', v)} />

                    {errors.code && (
                        <p className="text-red-600 text-sm mt-1">{errors.code}</p>
                    )}
                </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6">
                <Button
                    bsStyle={Button.DEFAULT}
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button
                    bsStyle={Button.PRIMARY}
                    type="submit"
                    disabled={loading}
                    iconName={loading ? undefined : "rioglyph-check"}
                    onClick={handleSubmit}
                >
                    {loading ? (
                        <div className="flex items-center">
                            <Spinner />
                        </div>
                    ) : (
                        'Adicionar'
                    )}
                </Button>
            </div>
        </form>
    );
};

export default SubTopicForm;

